import React, { useEffect, useRef, useState } from "react";
import { Phone, Question, Sparkle, Shield, Trophy, Checks, Warning, FilePdf, Printer, Lightbulb, BookOpen, ListDashes } from "@phosphor-icons/react";
import { teleproApi } from "../lib/teleproApi";

/**
 * Inline prospection tutorial — fetched from /api/telepro/me/tutorial.
 *
 * Renders a section list with collapsible bodies. When the user opens a
 * section, the section header is smooth-scrolled into view so the body
 * appears directly under the click point (critical on mobile where the
 * body would otherwise expand off-screen).
 *
 * Body markdown is minimal: **bold**, line breaks, and emoji checkmarks
 * pass through. We deliberately do NOT pull a full markdown library to
 * keep the bundle lean.
 */
const ICONS = {
  phone: Phone,
  question: Question,
  sparkle: Sparkle,
  shield: Shield,
  trophy: Trophy,
  checks: Checks,
  warning: Warning,
};

export default function TeleproTutorial() {
  const [data, setData] = useState(null);
  // We use a Set so multiple sections can be open at once (e.g. when the
  // telepro clicks "Tout déplier" for a one-shot read-through).
  const [openIds, setOpenIds] = useState(() => new Set(["intro"]));
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState("");
  // We keep a ref per section so we can scrollIntoView() the right one.
  const sectionRefs = useRef({});

  useEffect(() => {
    teleproApi.get("/me/tutorial")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Chargement impossible"));
  }, []);

  const toggleSection = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    // Smooth-scroll the section header into view when we're opening it.
    // (Skipped when closing — the user usually doesn't want to be relocated.)
    if (!openIds.has(id)) {
      requestAnimationFrame(() => {
        const el = sectionRefs.current[id];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  };

  const expandAll = () => {
    if (!data) return;
    setOpenIds(new Set(data.sections.map((s) => s.id)));
    // After opening everything, jump back to the first section so the user
    // starts at the top of the read-through.
    requestAnimationFrame(() => {
      const first = data.sections[0]?.id;
      const el = first ? sectionRefs.current[first] : null;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const collapseAll = () => setOpenIds(new Set());

  const downloadPdf = async (kind) => {
    // kind: "tutorial" | "pitch"
    setDownloading(kind);
    try {
      const path = kind === "pitch" ? "/me/pitch.pdf" : "/me/tutorial.pdf";
      const res = await teleproApi.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "pitch" ? "fiche-pitch.pdf" : "guide-prospection.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Téléchargement impossible");
    } finally {
      setDownloading("");
    }
  };

  if (error) return <p className="text-sm text-secondary">{error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Chargement du guide…</p>;

  return (
    <div data-testid="telepro-tutorial">
      {/* Strong recommendation banner — visible BEFORE the download buttons */}
      <div
        className="bg-primary/10 border-l-4 border-primary rounded-sm p-3 sm:p-4 mb-4"
        data-testid="tutorial-print-recommendation"
      >
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-sm bg-primary text-primary-foreground shrink-0">
            <Printer size={16} weight="bold"/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm sm:text-base text-primary mb-1">
              📄 Imprime ton guide complet et garde-le à portée de main
            </div>
            <p className="text-xs sm:text-[13px] leading-relaxed text-foreground/90">
              Si tu travailles sur <b>smartphone ou tablette</b>, tu ne pourras pas relire ton guide en même temps
              qu&apos;un appel. <b>Imprime le guide PDF ci-dessous</b> et garde-le à côté de toi — comme ça
              tu peux suivre les réponses tranquillement pendant que le prospect te parle, et trouver instantanément
              la bonne réponse à ses questions. <b>Astuce&nbsp;:</b> ajoute aussi la <b>fiche pitch A4</b> à imprimer
              et colle-la au-dessus du téléphone.
            </p>
          </div>
        </div>
      </div>

      {/* Print-friendly PDF downloads */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <button
          type="button"
          onClick={() => downloadPdf("tutorial")}
          disabled={downloading === "tutorial"}
          className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="tutorial-download-pdf"
        >
          <FilePdf size={16} weight="bold"/>
          {downloading === "tutorial" ? "Préparation…" : "Télécharger le guide complet (PDF)"}
        </button>
        <button
          type="button"
          onClick={() => downloadPdf("pitch")}
          disabled={downloading === "pitch"}
          className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="pitch-card-download-pdf"
        >
          <Printer size={16} weight="bold"/>
          {downloading === "pitch" ? "Préparation…" : "Fiche pitch A4 (à coller au mur)"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4 leading-relaxed flex items-start gap-1.5">
        <Lightbulb size={11} weight="duotone" className="shrink-0 mt-0.5 text-secondary"/>
        Le guide complet (≈ 17 pages) contient toutes les réponses détaillées. La fiche pitch est une A4 condensée
        à coller à côté de toi pendant tes appels.
      </p>

      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          Sections du guide ({data.sections.length})
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={expandAll}
            className="text-[11px] font-semibold text-primary hover:bg-primary/10 border border-primary/40 rounded-sm px-2 py-1 inline-flex items-center gap-1"
            data-testid="tutorial-expand-all"
            title="Ouvre toutes les sections pour une lecture complète"
          >
            <BookOpen size={11} weight="bold"/> Tout déplier
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-border rounded-sm px-2 py-1 inline-flex items-center gap-1"
            data-testid="tutorial-collapse-all"
            title="Replie toutes les sections"
          >
            <ListDashes size={11} weight="bold"/> Tout replier
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm divide-y divide-border">
        {data.sections.map((s) => {
          const Icon = ICONS[s.icon] || Phone;
          const open = openIds.has(s.id);
          return (
            <div
              key={s.id}
              ref={(el) => { sectionRefs.current[s.id] = el; }}
              className="scroll-mt-20"
              data-testid={`tutorial-section-${s.id}`}
            >
              <button
                type="button"
                onClick={() => toggleSection(s.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/40 transition"
                data-testid={`tutorial-toggle-${s.id}`}
                aria-expanded={open}
              >
                <span className={`w-8 h-8 grid place-items-center rounded-sm shrink-0 ${
                  s.icon === "warning" ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary"
                }`}>
                  <Icon size={16} weight="duotone"/>
                </span>
                <span className="flex-1 font-display font-medium text-sm sm:text-base">{s.title}</span>
                <span className={`text-xs font-bold text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
              </button>
              {open && (
                <div className="px-4 pb-4 pl-14 text-sm text-foreground/90 leading-relaxed whitespace-pre-line" data-testid={`tutorial-body-${s.id}`}>
                  {renderBody(s.body)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {data.quick_links.map((l) => (
          <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
             className="text-xs bg-muted/40 hover:bg-accent border border-border rounded-sm px-3 py-2 text-center font-medium">
            {l.label} ↗
          </a>
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground mt-3 text-center">
        Guide v{data.version} — mis à jour par l&apos;équipe MonCommerce68.
      </p>
    </div>
  );
}

/**
 * Render minimal markdown: **bold** within text, preserve line breaks.
 * Whitespace pre-line is set on the wrapper so \n becomes a visual break.
 */
function renderBody(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <b key={i} className="text-foreground">{part.slice(2, -2)}</b>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
